import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EvidenceUpload = ({ evidence, onChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    processFiles(files);
  };

  const processFiles = (files) => {
    const newEvidence = files?.map((file) => {
      const fileType = file?.type?.startsWith('image/') ? 'image' : 
                       file?.type?.startsWith('video/') ? 'video' : 'document';
      
      return {
        id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
        type: fileType,
        file,
        url: URL.createObjectURL(file),
        name: file?.name,
        size: file?.size,
        alt: `Evidence file: ${file?.name}`
      };
    });
    onChange([...evidence, ...newEvidence]);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    processFiles(files);
  };

  const handleRemove = (evidenceId) => {
    const updatedEvidence = evidence?.filter(item => item?.id !== evidenceId);
    onChange(updatedEvidence);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'document':
        return 'FileText';
      default:
        return 'File';
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
          isDragging
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Upload" size={32} color="var(--color-primary)" strokeWidth={2} />
          </div>
          <div>
            <p className="font-body text-base text-foreground mb-1">
              Drag and drop files here, or
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="Upload"
            >
              Browse Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          <p className="font-caption text-xs text-muted-foreground">
            Supported: Images, Videos, PDF, Word documents (Max 10MB per file)
          </p>
        </div>
      </div>

      {/* Evidence List */}
      {evidence?.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-body font-semibold text-foreground">
            Uploaded Evidence ({evidence?.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {evidence?.map((item) => (
              <div
                key={item?.id}
                className="bg-muted/30 rounded-lg p-4 flex items-start space-x-3 border border-border"
              >
                {/* Preview */}
                <div className="flex-shrink-0">
                  {item?.type === 'image' ? (
                    <img
                      src={item?.url}
                      alt={item?.alt}
                      className="w-16 h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-primary/10 rounded flex items-center justify-center">
                      <Icon
                        name={getFileIcon(item?.type)}
                        size={24}
                        className="text-primary"
                        strokeWidth={2}
                      />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-semibold text-foreground truncate">
                    {item?.name}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground">
                    {item?.type?.charAt(0)?.toUpperCase() + item?.type?.slice(1)} • {formatFileSize(item?.size)}
                  </p>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => handleRemove(item?.id)}
                  className="flex-shrink-0 p-1 rounded hover:bg-error/10 transition-smooth"
                >
                  <Icon name="X" size={18} className="text-error" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceUpload;